import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircle, Circle, Calendar, Filter, Plus, 
  Trash2, AlertCircle, Clock, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Group tasks by week
const groupTasksByWeek = (tasks) => {
  const groups = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  tasks.forEach(task => {
    const dueDate = new Date(task.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    // Calculate week start (Monday)
    const weekStart = new Date(dueDate);
    const day = weekStart.getDay();
    const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
    weekStart.setDate(diff);
    
    const weekKey = weekStart.toISOString().split('T')[0];
    
    if (!groups[weekKey]) {
      groups[weekKey] = {
        weekStart,
        tasks: [],
        label: getWeekLabel(weekStart, today)
      };
    }
    groups[weekKey].tasks.push(task);
  });
  
  // Sort groups by week
  return Object.values(groups).sort((a, b) => a.weekStart - b.weekStart);
};

const getWeekLabel = (weekStart, today) => {
  const diffDays = Math.floor((weekStart - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < -7) return `Week of ${weekStart.toLocaleDateString()}`;
  if (diffDays < 0) return 'Last Week';
  if (diffDays < 7) return 'This Week';
  if (diffDays < 14) return 'Next Week';
  return `Week of ${weekStart.toLocaleDateString()}`;
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'high': return 'text-danger border-danger';
    case 'medium': return 'text-warning border-warning';
    default: return 'text-info border-info';
  }
};

const TasksPanel = ({ farmId, fieldId = null }) => {
  const { getAuthHeaders } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open'); // 'all', 'open', 'done'
  const [fieldFilter, setFieldFilter] = useState(fieldId || 'all');
  const [fields, setFields] = useState([]);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    details: '',
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'medium',
    fieldId: fieldId || ''
  });

  useEffect(() => {
    loadTasks();
    loadFields();
  }, [farmId]);

  const loadTasks = async () => {
    try {
      const response = await axios.get(
        `${API}/farms/${farmId}/tasks`,
        { headers: getAuthHeaders() }
      );
      setTasks(response.data || []);
      
      // Expand current week by default
      const today = new Date();
      const weekStart = new Date(today);
      const day = weekStart.getDay();
      const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
      weekStart.setDate(diff);
      weekStart.setHours(0, 0, 0, 0);
      setExpandedWeeks({ [weekStart.toISOString().split('T')[0]]: true });
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      const response = await axios.get(
        `${API}/fields`,
        { headers: getAuthHeaders() }
      );
      setFields(response.data || []);
    } catch (error) {
      console.error('Failed to load fields:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/farms/${farmId}/tasks`,
        {
          ...newTask,
          source: 'manual'
        },
        { headers: getAuthHeaders() }
      );
      
      setTasks(prev => [response.data, ...prev]);
      setShowAddModal(false);
      setNewTask({
        title: '',
        details: '',
        dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        priority: 'medium',
        fieldId: fieldId || ''
      });
      toast.success('Task created');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleToggleStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'open' : 'done';
    
    try {
      await axios.patch(
        `${API}/tasks/${taskId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, status: newStatus, completedAt: newStatus === 'done' ? new Date().toISOString() : null }
          : t
      ));
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    
    try {
      await axios.delete(
        `${API}/tasks/${taskId}`,
        { headers: getAuthHeaders() }
      );
      
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Filter tasks
  let filteredTasks = tasks;
  if (filter !== 'all') {
    filteredTasks = filteredTasks.filter(t => t.status === filter);
  }
  if (fieldFilter !== 'all') {
    filteredTasks = filteredTasks.filter(t => t.fieldId === fieldFilter);
  }

  const weekGroups = groupTasksByWeek(filteredTasks);

  const toggleWeek = (weekKey) => {
    setExpandedWeeks(prev => ({ ...prev, [weekKey]: !prev[weekKey] }));
  };

  if (loading) {
    return <div className="animate-pulse py-8 text-center">Loading tasks...</div>;
  }

  return (
    <div className="space-y-4" data-testid="tasks-panel">
      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden">
            {['all', 'open', 'done'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm capitalize ${
                  filter === f ? 'bg-primary text-white' : 'hover:bg-muted'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          
          {!fieldId && (
            <select
              value={fieldFilter}
              onChange={(e) => setFieldFilter(e.target.value)}
              className="text-sm border rounded-lg px-2 py-1.5"
            >
              <option value="all">All Fields</option>
              {fields.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary text-sm flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Task
        </button>
      </div>

      {/* Summary */}
      <div className="flex gap-4 text-sm">
        <span className="text-muted-foreground">
          {tasks.filter(t => t.status === 'open').length} open
        </span>
        <span className="text-muted-foreground">
          {tasks.filter(t => t.status === 'done').length} done
        </span>
      </div>

      {/* Task Groups by Week */}
      {weekGroups.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No tasks found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {weekGroups.map(group => {
            const weekKey = group.weekStart.toISOString().split('T')[0];
            const isExpanded = expandedWeeks[weekKey];
            const openCount = group.tasks.filter(t => t.status === 'open').length;
            
            return (
              <div key={weekKey} className="card overflow-hidden">
                <button
                  onClick={() => toggleWeek(weekKey)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                    <span className="font-medium">{group.label}</span>
                    <span className="text-sm text-muted-foreground">
                      ({group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {openCount > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-warning-light text-warning-foreground rounded-full">
                      {openCount} open
                    </span>
                  )}
                </button>
                
                {isExpanded && (
                  <div className="border-t divide-y">
                    {group.tasks.map(task => {
                      const field = fields.find(f => f.id === task.fieldId);
                      const isOverdue = task.status === 'open' && new Date(task.dueDate) < new Date();
                      
                      return (
                        <div
                          key={task.id}
                          className={`p-4 flex items-start gap-3 ${
                            task.status === 'done' ? 'opacity-60' : ''
                          }`}
                          data-testid={`task-${task.id}`}
                        >
                          <button
                            onClick={() => handleToggleStatus(task.id, task.status)}
                            className={`flex-shrink-0 mt-0.5 ${
                              task.status === 'done' ? 'text-success' : 'text-muted-foreground hover:text-primary'
                            }`}
                          >
                            {task.status === 'done' ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <Circle className="w-5 h-5" />
                            )}
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`font-medium ${task.status === 'done' ? 'line-through' : ''}`}>
                                {task.title}
                              </span>
                              {task.priority && task.priority !== 'low' && (
                                <span className={`px-1.5 py-0.5 text-xs border rounded ${getPriorityColor(task.priority)}`}>
                                  {task.priority}
                                </span>
                              )}
                              {task.source === 'alert' && (
                                <AlertCircle className="w-4 h-4 text-warning" />
                              )}
                            </div>
                            
                            {task.details && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {task.details}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <span className={`flex items-center gap-1 ${isOverdue ? 'text-danger' : ''}`}>
                                <Calendar className="w-3 h-3" />
                                {new Date(task.dueDate).toLocaleDateString()}
                                {isOverdue && ' (overdue)'}
                              </span>
                              {field && (
                                <span>Field: {field.name}</span>
                              )}
                            </div>
                          </div>
                          
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="p-1 text-muted-foreground hover:text-danger rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="card p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-4">Create Task</h3>
            
            <div className="space-y-4">
              <div>
                <label className="input-label">Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  className="input-field"
                  placeholder="What needs to be done?"
                />
              </div>
              
              <div>
                <label className="input-label">Details</label>
                <textarea
                  value={newTask.details}
                  onChange={(e) => setNewTask(prev => ({ ...prev, details: e.target.value }))}
                  className="input-field"
                  rows={2}
                  placeholder="Optional details..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Due Date</label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="input-field"
                  />
                </div>
                
                <div>
                  <label className="input-label">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                    className="input-field"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              
              {!fieldId && (
                <div>
                  <label className="input-label">Field (optional)</label>
                  <select
                    value={newTask.fieldId}
                    onChange={(e) => setNewTask(prev => ({ ...prev, fieldId: e.target.value }))}
                    className="input-field"
                  >
                    <option value="">No specific field</option>
                    {fields.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateTask}
                className="btn-primary flex-1"
              >
                Create Task
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Export a function to create task from alert
export const createTaskFromAlert = async (alert, fieldId, farmId, getAuthHeaders) => {
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 3);
  
  const task = {
    title: `${alert.title} - ${alert.type}`,
    details: alert.summary || alert.why?.join('; ') || '',
    source: 'alert',
    sourceId: alert.id,
    fieldId,
    dueDate: dueDate.toISOString().split('T')[0],
    priority: alert.severity === 'high' ? 'high' : alert.severity === 'medium' ? 'medium' : 'low'
  };

  try {
    const response = await axios.post(
      `${API}/farms/${farmId}/tasks`,
      task,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default TasksPanel;
